/* global PowerPoint, Excel */

import { isOfficeJsBypassed } from "./featureFlagService";

export { isOfficeJsBypassed };

let hasLoggedPowerPointBypass = false;
let hasLoggedExcelBypass = false;
let powerPointRunTail: Promise<void> = Promise.resolve();

const PREVIOUS_CALL_BUSY_ERROR_SUBSTRING = "wait until the previous call completes";
const POWERPOINT_BUSY_RETRY_DELAY_MS = 150;

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  const maybeMessage = (error as { message?: unknown } | null)?.message;
  if (typeof maybeMessage === "string") {
    return maybeMessage;
  }
  return String(error);
}

function isPreviousCallBusyError(error: unknown): boolean {
  return toErrorMessage(error).toLowerCase().includes(PREVIOUS_CALL_BUSY_ERROR_SUBSTRING);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function runPowerPointWithBusyRetry<T>(
  callback: (context: PowerPoint.RequestContext) => Promise<T>
): Promise<T> {
  try {
    return await PowerPoint.run(callback);
  } catch (error) {
    if (!isPreviousCallBusyError(error)) {
      throw error;
    }
    console.warn("[OfficeJsGuard] PowerPoint busy; retrying once after short delay");
    await sleep(POWERPOINT_BUSY_RETRY_DELAY_MS);
    return PowerPoint.run(callback);
  }
}

function enqueuePowerPointRun<T>(operation: () => Promise<T>): Promise<T> {
  const queuedOperation = powerPointRunTail.then(operation, operation);
  // Keep the queue alive after failures so future calls still run.
  powerPointRunTail = queuedOperation.then(
    () => undefined,
    () => undefined
  );
  return queuedOperation;
}

/**
 * Wraps PowerPoint.run with the bypassOfficeJsApi feature flag check.
 * When bypassed, the callback is never executed and `nopDefault` is returned.
 */
export async function guardedPowerPointRun<T = void>(
  callback: (context: PowerPoint.RequestContext) => Promise<T>,
  nopDefault?: T
): Promise<T> {
  if (isOfficeJsBypassed()) {
    if (!hasLoggedPowerPointBypass) {
      console.log("[OfficeJsGuard] PowerPoint.run bypassed (NOP mode)");
      hasLoggedPowerPointBypass = true;
    }
    return nopDefault as T;
  }
  if (typeof PowerPoint === "undefined" || !PowerPoint.run) {
    console.warn("[OfficeJsGuard] PowerPoint global unavailable — returning NOP default");
    return nopDefault as T;
  }
  return enqueuePowerPointRun(() => runPowerPointWithBusyRetry(callback));
}

/**
 * Wraps Excel.run with the bypassOfficeJsApi feature flag check.
 * When bypassed, the callback is never executed and `nopDefault` is returned.
 */
export async function guardedExcelRun<T = void>(
  callback: (context: Excel.RequestContext) => Promise<T>,
  nopDefault?: T
): Promise<T> {
  if (isOfficeJsBypassed()) {
    if (!hasLoggedExcelBypass) {
      console.log("[OfficeJsGuard] Excel.run bypassed (NOP mode)");
      hasLoggedExcelBypass = true;
    }
    return nopDefault as T;
  }
  if (typeof Excel === "undefined" || !Excel.run) {
    console.warn("[OfficeJsGuard] Excel global unavailable — returning NOP default");
    return nopDefault as T;
  }
  return Excel.run(callback);
}
